// Computes the line pricing for a customised product.
//
// Output:
//   { basePrice, perUnit, qty, multiplierTotal, deltaTotal, addonTotal, lineTotal,
//     breakdown: [{ label, amount }] }
//
// Rules (must match spec §6):
//   perUnit = basePrice × ∏(multipliers) + Σ(deltas) + Σ(addonPrices)
//   line    = perUnit × qty
//
// Only CHP/TGL/RDC/LST carry multipliers/deltas on their selected option.
// MCH carries deltas only, summed per selected option. ADG items contribute
// fixed `price` each when selected. QTY is the multiplier on the final line.

export function computePricing(cfg, values, basePrice, defaultQty = 1) {
  const breakdown = [];
  let multiplierTotal = 1;
  let deltaTotal = 0;
  let addonTotal = 0;
  let qty = defaultQty;

  (cfg.fields || []).forEach((f) => {
    const val = values[f.name];

    if (f.type === "QTY") {
      const q = Number(val) || defaultQty;
      qty = q;
      return;
    }

    // NUM fields with priceImpact:"addon" contribute their entered value
    // as an additional charge (e.g. "Additional Charges" free-form input).
    if (f.type === "NUM" && f.priceImpact === "addon") {
      const n = Number(val) || 0;
      if (n > 0) {
        addonTotal += n;
        breakdown.push({ label: f.name, amount: n });
      }
      return;
    }

    if (f.type === "ADG") {
      (val || []).forEach((id) => {
        const it = (f.items || []).find((x) => x.id === id);
        if (!it) return;
        addonTotal += it.price;
        breakdown.push({ label: `${it.name}`, amount: it.price });
      });
      return;
    }

    if (["CHP", "TGL", "RDC", "LST"].includes(f.type)) {
      const opt = (f.options || []).find((o) => o.value === val);
      if (!opt) return;
      if (opt.priceMultiplier && opt.priceMultiplier > 0) {
        multiplierTotal *= Number(opt.priceMultiplier);
        if (opt.priceMultiplier !== 1) {
          breakdown.push({
            label: `${f.name}: ${opt.label} (${opt.priceMultiplier}×)`,
            amount: Math.round(basePrice * (opt.priceMultiplier - 1)),
          });
        }
      }
      if (opt.priceDelta) {
        deltaTotal += Number(opt.priceDelta);
        breakdown.push({
          label: `${f.name}: ${opt.label}`,
          amount: Number(opt.priceDelta),
        });
      }
      return;
    }

    if (f.type === "MCH") {
      (val || []).forEach((v) => {
        const opt = (f.options || []).find((o) => o.value === v);
        if (opt && opt.priceDelta) {
          deltaTotal += Number(opt.priceDelta);
          breakdown.push({
            label: `${f.name}: ${opt.label}`,
            amount: Number(opt.priceDelta),
          });
        }
      });
      return;
    }
  });

  const perUnit = Math.max(0, basePrice * multiplierTotal + deltaTotal + addonTotal);
  const lineTotal = Math.round(perUnit * qty);

  return {
    basePrice,
    multiplierTotal,
    deltaTotal,
    addonTotal,
    perUnit,
    qty,
    lineTotal,
    breakdown,
    baseAfterMultiplier: Math.round(basePrice * multiplierTotal),
    customisationDelta: Math.round(basePrice * multiplierTotal - basePrice + deltaTotal),
  };
}
